// src/components/Notebook/NoteAudioPlayer.js
// Wrapper component that fetches signed URL and displays audio player for notes with audio
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { GraphicEq as AudioIcon } from '@mui/icons-material';
import AudioPlayer from '../AudioRecorder/AudioPlayer';
import api from '../../services/api';

/**
 * Extract GCS path from note content (for notes created before audioMetadata was added)
 * Looks for gs:// paths in the HTML content
 */
const extractGcsPathFromContent = (content) => {
  if (!content) return null;
  // Match gs://bucket/path pattern
  const match = content.match(/gs:\/\/[^\s<"]+/);
  return match ? match[0] : null;
};

/**
 * Extract duration from note content or tags
 */
const extractDurationFromNote = (note) => {
  // Check tags for duration
  if (note?.tags) {
    const durationTag = note.tags.find(tag => tag.startsWith('duration-'));
    if (durationTag) {
      return durationTag.replace('duration-', '');
    }
  }
  // Try to extract from content
  if (note?.content) {
    const match = note.content.match(/Duration:\s*([0-9:]+)/i);
    if (match) return match[1];
  }
  return '0:00';
};

/**
 * NoteAudioPlayer - Displays audio player for notes with audio attachments
 * Fetches signed URL on mount and handles loading/error states
 *
 * @param {object} note - The note object containing audioMetadata
 * @param {string} entityId - Entity ID for permission checks
 * @param {boolean} compact - Use compact styling
 */
const NoteAudioPlayer = ({ note, entityId, compact = false }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioMetadata, setAudioMetadata] = useState(null);

  // Get audio GCS path - from audioMetadata field or fallback to parsing content
  const audioGcsPath = useMemo(() => {
    if (note?.audioMetadata?.audioGcsPath) {
      return note.audioMetadata.audioGcsPath;
    }
    // Fallback: extract from content for legacy notes
    return extractGcsPathFromContent(note?.content);
  }, [note?.audioMetadata?.audioGcsPath, note?.content]);

  useEffect(() => {
    const fetchAudioUrl = async () => {
      if (!note?.id || !audioGcsPath || !entityId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Pass gcsPath for legacy notes that don't have audioMetadata field
        const response = await api.getNoteAudioUrl(note.id, entityId, audioGcsPath);

        if (response.success && response.signedUrl) {
          setAudioUrl(response.signedUrl);
          setAudioMetadata(response.audioMetadata);
        } else {
          setError(response.error || 'Failed to load audio');
        }
      } catch (err) {
        console.error('Failed to fetch audio URL:', err);
        setError(err.message || 'Failed to load audio');
      } finally {
        setLoading(false);
      }
    };

    fetchAudioUrl();
  }, [note?.id, audioGcsPath, entityId]);

  // Don't render if note doesn't have audio
  if (!audioGcsPath) {
    return null;
  }

  // Get duration from metadata or extract from note
  const duration = audioMetadata?.duration || note?.audioMetadata?.duration || extractDurationFromNote(note);

  // Error fetching URL (not playback error)
  if (error && !loading && !audioUrl) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Unable to load audio: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: compact ? 1.5 : 2 }}>
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            color: 'text.secondary',
          }}
        >
          <AudioIcon sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Audio Recording
          </Typography>
        </Box>
      )}
      <AudioPlayer
        src={audioUrl}
        duration={duration}
        loading={loading}
        compact={compact}
        onError={(err) => {
          console.error('Audio playback error:', err);
          // Don't set error state here - let AudioPlayer handle displaying errors
        }}
      />
    </Box>
  );
};

export default NoteAudioPlayer;
